import type IIntegratorRunLog from "../../interfaces/models/IIntegratorRunLog";
import { formatRegionDisplay } from "../../utils/formatRegionDisplay";

/** Shown for a firm field the dashboard still lists when the source value is blank. */
const NO_VALUE = "—";

export interface ActiveSyncConfiguration {
  firmId: string;
  enabled: boolean;
  feedUrl?: string;
}

export interface ActiveSyncFirmRecord {
  id: string;
  name?: string | null;
  firmName?: string | null;
  shortId?: string | number | null;
  regionNames?: unknown;
  membershipStatus?: unknown;
}

export interface ActiveSyncFirmRow {
  id: string;
  firmName: string;
  name: string;
  shortId: string;
  regionNames: string;
  membershipStatus: string;
  integratorEnabled: "Yes" | "No" | "—";
  feedUrl: string;
  /** Epoch milliseconds. Null when the firm has no recorded run. */
  latestImport: number | null;
  hasIntegratorConfig: boolean;
  latestRunLog: IIntegratorRunLog | null;
}

export type ShowFirmsSelection = "configured" | "not-configured" | "all";

function text(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return "";
}

function displayFirmName(
  firm: ActiveSyncFirmRecord | undefined,
  firmId: string
): string {
  const legalName = text(firm?.firmName);
  if (legalName) {
    return legalName;
  }
  const name = text(firm?.name);
  if (name) {
    return name;
  }
  return firmId;
}

function recordedOrEmpty(value: unknown): string {
  const recorded = text(value);
  if (recorded) {
    return recorded;
  }
  return NO_VALUE;
}

function latestImportTimestamp(log: IIntegratorRunLog | undefined): number | null {
  const queuedDate = log?.queuedDate;
  if (typeof queuedDate !== "number" || !Number.isFinite(queuedDate) || queuedDate <= 0) {
    return null;
  }
  return queuedDate;
}

function toDashboardRow(
  firmId: string,
  firm: ActiveSyncFirmRecord | undefined,
  configuration: ActiveSyncConfiguration | undefined,
  latestRunLog: IIntegratorRunLog | null
): ActiveSyncFirmRow {
  const firmName = displayFirmName(firm, firmId);
  const region = formatRegionDisplay(firm?.regionNames);
  return {
    id: firmId,
    firmName,
    name: firmName,
    shortId: text(firm?.shortId),
    regionNames: region || NO_VALUE,
    membershipStatus: recordedOrEmpty(firm?.membershipStatus),
    integratorEnabled: configuration ? (configuration.enabled ? "Yes" : "No") : NO_VALUE,
    feedUrl: text(configuration?.feedUrl) || NO_VALUE,
    latestImport: latestImportTimestamp(latestRunLog ?? undefined),
    hasIntegratorConfig: configuration != null,
    latestRunLog,
  };
}

/**
 * Dashboard rows for the Show Firms selection. Firm-name order is the tie-break
 * the table keeps when two latest-import timestamps are equal.
 * Omitted selection is configured firms only.
 */
export function buildActiveSyncFirmRows(input: {
  configurations: ActiveSyncConfiguration[];
  firms: ActiveSyncFirmRecord[];
  latestLogs: IIntegratorRunLog[];
  showFirms?: ShowFirmsSelection;
}): ActiveSyncFirmRow[] {
  const showFirms = input.showFirms ?? "configured";
  const firmById = new Map(input.firms.map((firm) => [firm.id, firm]));
  const configByFirmId = new Map(
    input.configurations
      .filter((configuration) => text(configuration.firmId))
      .map((configuration) => [text(configuration.firmId), configuration])
  );
  const logByFirmId = new Map(input.latestLogs.map((log) => [log.firmId, log]));
  const rows: ActiveSyncFirmRow[] = [];

  if (showFirms === "configured") {
    for (const configuration of input.configurations) {
      const firmId = text(configuration.firmId);
      if (!firmId) {
        continue;
      }
      rows.push(
        toDashboardRow(
          firmId,
          firmById.get(firmId),
          configuration,
          logByFirmId.get(firmId) ?? null
        )
      );
    }
  } else {
    for (const firm of input.firms) {
      const firmId = text(firm.id);
      if (!firmId) {
        continue;
      }
      const configuration = configByFirmId.get(firmId);
      if (showFirms === "not-configured" && configuration) {
        continue;
      }
      rows.push(
        toDashboardRow(
          firmId,
          firm,
          configuration,
          configuration ? logByFirmId.get(firmId) ?? null : null
        )
      );
    }
    if (showFirms === "all") {
      for (const configuration of input.configurations) {
        const firmId = text(configuration.firmId);
        if (!firmId || firmById.has(firmId)) {
          continue;
        }
        rows.push(
          toDashboardRow(
            firmId,
            undefined,
            configuration,
            logByFirmId.get(firmId) ?? null
          )
        );
      }
    }
  }

  rows.sort((left, right) =>
    left.firmName.localeCompare(right.firmName, undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
  return rows;
}
